import { useEffect, useState } from 'react';
import Container from '@ui/container/container';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import { DateTimeLayout } from '../../../templates/widgets/date-time-layout';
import TabRow from '../controls/tab-row';
import BlockStylePanel from './block-style-panel';
import DateTimeLayoutPicker from './date-time-layout-picker';

const TABS: { value: 'styles' | 'layouts'; label: string }[] = [
  { value: 'styles', label: 'Styles' },
  { value: 'layouts', label: 'Layouts' },
];

interface DateTimeStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateProp: (key: string, value: unknown) => void;
  startTimestamp?: number | null;
  timezone?: string;
}

export default function DateTimeStylePanel({
  block,
  onUpdateStyle,
  onUpdateProp,
  startTimestamp,
  timezone,
}: DateTimeStylePanelProps) {
  const [activeTab, setActiveTab] = useState<'styles' | 'layouts'>('styles');
  // a template's own slot can start in another layout via its preset, which this panel can't see -
  // so until the user picks one, show whichever layout the preview actually rendered. A slot a
  // template draws itself (chateau's countdown, siena's info list) renders with no layout at all,
  // so it only gets the styles
  const [rendered, setRendered] = useState<{ found: boolean; layout?: DateTimeLayout }>({ found: false });
  useEffect(() => {
    const el = document.querySelector(`[data-block-id="${CSS.escape(block.id)}"]`);
    setRendered({ found: !!el, layout: (el?.getAttribute('data-layout') as DateTimeLayout | null) ?? undefined });
  }, [block.id]);
  const hasLayouts = !rendered.found || !!rendered.layout;
  const layout = (block.props.layout as DateTimeLayout | undefined) ?? rendered.layout ?? DateTimeLayout.Rows;

  if (!hasLayouts) {
    return <BlockStylePanel block={block} onUpdateStyle={onUpdateStyle} radiusLabel="Rounded corners" />;
  }

  return (
    <Container className="flex-column gap-16">
      <Container padding="0 16px">
        <TabRow tabs={TABS} value={activeTab} onChange={setActiveTab} />
      </Container>

      {activeTab === 'styles' ? (
        <BlockStylePanel block={block} onUpdateStyle={onUpdateStyle} radiusLabel="Rounded corners" />
      ) : (
        <Container padding="0 16px">
          <DateTimeLayoutPicker
            value={layout}
            onChange={(value) => onUpdateProp('layout', value)}
            startTimestamp={startTimestamp}
            timezone={timezone}
          />
        </Container>
      )}
    </Container>
  );
}
